// src/stores/noteNotificationStore.js
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { collection, doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { db } from '@/services/firebaseconfig'
import { useUserStore } from './userStore'

export const useNoteNotificationStore = defineStore('noteNotifications', () => {
  const userStore = useUserStore()

  const unreadNotesByCustomer = ref({})
  const isSubscribed = ref(false)
  let unsubscribeListener = null

  const totalUnreadCount = computed(() => {
    return Object.values(unreadNotesByCustomer.value).reduce(
      (sum, data) => sum + (data.total || 0),
      0
    )
  })

  const getCountsForCustomer = computed(() => {
    return customerId => {
      return (
        unreadNotesByCustomer.value[customerId] || {
          total: 0,
          byStage: {},
          byTask: {},
          taggedForMe: 0,
        }
      )
    }
  })

  const getUnreadCountForTask = computed(() => {
    return (customerId, taskId) => {
      return unreadNotesByCustomer.value[customerId]?.byTask?.[taskId] || 0
    }
  })

  const getUnreadCountForStage = computed(() => {
    return (customerId, stageName) => {
      return unreadNotesByCustomer.value[customerId]?.byStage?.[stageName] || 0
    }
  })

  function subscribe() {
    if (isSubscribed.value || !userStore.user?.uid) return

    const unreadNotesRef = collection(db, 'users', userStore.user.uid, 'unreadNotes')

    unsubscribeListener = onSnapshot(
      unreadNotesRef,
      snapshot => {
        const newUnreadMap = {}
        snapshot.forEach(doc => {
          const data = doc.data()
          newUnreadMap[doc.id] = {
            // doc.id is the customerId
            total: data.count ?? data.total ?? 0,
            byStage: data.byStage || {},
            byTask: data.byTask || {},
            taggedForMe: data.taggedForMe || 0,
            lastAt: data.lastAt?.toDate(),
          }
        })
        unreadNotesByCustomer.value = newUnreadMap
      },
      error => {
        console.error('[NoteNotificationStore] Error in onSnapshot listener:', error)
      }
    )

    isSubscribed.value = true
  }

  function unsubscribe() {
    if (unsubscribeListener) {
      unsubscribeListener()
      unsubscribeListener = null
    }
    unreadNotesByCustomer.value = {}
    isSubscribed.value = false
  }

  async function markNoteAsRead(note) {
    const uid = userStore.user?.uid
    if (!uid || !note?.id || !note?.customerId) return

    try {
      const receiptRef = doc(db, `customers/${note.customerId}/notes/${note.id}/readReceipts`, uid)
      await setDoc(receiptRef, { readAt: serverTimestamp() })
    } catch (error) {
      console.error('Failed to write read receipt for note:', note.id, error)
    }
  }

  async function clearTaggedNotesForStage(customerId, stageName) {
    const uid = userStore.user?.uid
    if (!uid) return
    console.log(
      `Clearing tagged notes for stage ${stageName} is now handled by marking individual notes as read.`
    )
  }

  return {
    unreadNotesByCustomer,
    isSubscribed,
    totalUnreadCount,
    getCountsForCustomer,
    getUnreadCountForTask,
    getUnreadCountForStage,
    subscribe,
    unsubscribe,
    startListener: subscribe,
    stopListener: unsubscribe,
    markNoteAsRead,
    clearTaggedNotesForStage,
  }
})
